// Implements: F-UIX-019
/**
 * The platform totals as rows a view can show: organization level only (UIX.8, RPT.5).
 *
 * Every field of a row is picked by name from the wire shape, never spread: whatever else
 * an answer might carry (a team, a person, an e-mail) has no field to land in, so it can
 * never be rendered. An organization is named from the organization list when the list
 * knows it, and by its id otherwise.
 *
 * The "No organization" row exists only when that total is non-zero (P6): the server sends
 * `null` when there was no such generation, and a total of nothing but zeros is treated
 * the same way, so the row never shows an empty line.
 */

import { PLATFORM_LOCALE } from "../../../types/locale";
import type { CostView, PlatformTotalsView, TotalsView } from "../../../types/reports";
import { costText } from "./money";

/** One amount of money of a row. */
export interface RowCost {
  readonly currency: string;
  readonly micros: number;
}

/** The figures every row shows. */
export interface RowFigures {
  readonly delivered: number;
  readonly failed: number;
  readonly cancelled: number;
  /** Delivered, failed and cancelled together: what the chart draws. */
  readonly generations: number;
  readonly promptTokens: number;
  readonly completionTokens: number;
  readonly costs: readonly RowCost[];
}

/** An organization's row (named, or by id when the list does not name it), or the "No organization" row. */
export type TotalsRow =
  | (RowFigures & {
      readonly kind: "organization";
      readonly key: string;
      readonly organizationId: string;
      readonly name: string | null;
    })
  | (RowFigures & { readonly kind: "none"; readonly key: typeof NO_ORGANIZATION_KEY });

/** The key of the "No organization" row: never a UUID, so never an organization's. */
export const NO_ORGANIZATION_KEY = "no-organization";

function costsOf(costs: readonly CostView[]): RowCost[] {
  return costs.map((cost) => ({ currency: cost.currency, micros: cost.cost_micros }));
}

/** The figures of one total, field by field. */
export function figuresOf(totals: TotalsView): RowFigures {
  return {
    delivered: totals.delivered,
    failed: totals.failed,
    cancelled: totals.cancelled,
    generations: totals.delivered + totals.failed + totals.cancelled,
    promptTokens: totals.prompt_tokens,
    completionTokens: totals.completion_tokens,
    costs: costsOf(totals.costs),
  };
}

/** Whether a "No organization" total is worth a row: present and not all zeros. */
export function hasNoOrganization(totals: TotalsView | null): totals is TotalsView {
  if (totals === null) return false;
  const figures = figuresOf(totals);
  return (
    figures.generations > 0 ||
    figures.promptTokens > 0 ||
    figures.completionTokens > 0 ||
    figures.costs.some((cost) => cost.micros !== 0)
  );
}

/**
 * The rows of the view: organizations by name (unnamed ones after, by id), then the
 * "No organization" row when it is non-zero. The overall total is not a row here.
 *
 * @param view The server's answer.
 * @param names Organization names by id, from `GET /api/organizations` (empty when unknown).
 */
export function platformRows(view: PlatformTotalsView, names: ReadonlyMap<string, string>): TotalsRow[] {
  const organizations: TotalsRow[] = view.organizations.map((line) => ({
    kind: "organization",
    key: line.organization_id,
    organizationId: line.organization_id,
    name: names.get(line.organization_id) ?? null,
    ...figuresOf(line),
  }));
  organizations.sort(byName);
  if (hasNoOrganization(view.no_organization)) {
    organizations.push({ kind: "none", key: NO_ORGANIZATION_KEY, ...figuresOf(view.no_organization) });
  }
  return organizations;
}

function sortName(row: TotalsRow): readonly [number, string] {
  if (row.kind === "none") return [2, ""];
  return row.name === null ? [1, row.organizationId] : [0, row.name];
}

function byName(left: TotalsRow, right: TotalsRow): number {
  const [leftRank, leftName] = sortName(left);
  const [rightRank, rightName] = sortName(right);
  return leftRank - rightRank || leftName.localeCompare(rightName, PLATFORM_LOCALE);
}

/** `id -> name`, from the organization list. */
export function namesById(list: readonly { readonly id: string; readonly name: string }[]): Map<string, string> {
  return new Map(list.map((organization) => [organization.id, organization.name]));
}

/** What a row is called: the organization's name, its id when unnamed, or the "No organization" label. */
export function rowLabel(row: TotalsRow, noOrganization: string): string {
  if (row.kind === "none") return noOrganization;
  return row.name ?? row.organizationId;
}

/**
 * Each organization's spend as one text (mockup v3 P3, Plataforma > Organizaciones): every currency
 * it spent in, in the order the server sent them, joined by " · ". An organization that spent
 * nothing has no entry, so its cell shows "—".
 */
export function organizationSpend(totals: PlatformTotalsView): Map<string, string> {
  const spend = new Map<string, string>();
  for (const row of totals.organizations) {
    if (row.costs.length === 0) continue;
    spend.set(row.organization_id, row.costs.map((cost) => costText(cost.cost_micros, cost.currency)).join(" · "));
  }
  return spend;
}
